// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./PropertyConsole.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import { Button, CircularProgress, IconButton } from "@mui/material";

// MUI ICONS
import EditIcon from "@mui/icons-material/Edit";

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import PropertyConsoleEditAddDialog from "./PropertyConsoleEditAddDialog/PropertyConsoleEditAddDialog";
import TanStackTable from "../../Misc/Table/TanStackTable";

// CONSTANTS
import { convertToPST } from "../../../constants/dateTime";
import {
	dateSort,
	defaultSort,
	notApplicable,
} from "../../Misc/Table/TanStackTableConstants";

// OTHER

// REACT COMPONENT
const PropertyConsole = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	const [properties, setProperties] = useState([]);

	const [propertyRows, setPropertyRows] = useState([]);
	const [propertyColumns, setPropertyColumns] = useState([]);

	const [nameSortDirection, setNameSortDirection] = useState("none");
	const [createdSortDirection, setCreatedSortDirection] = useState("none");

	const [offices, setOffices] = useState([]);

	const [recordToEdit, setRecordToEdit] = useState({});
	const [loading, setLoading] = useState(false);
	const [editAddDialogOpen, setEditAddDialogOpen] = useState(false);
	const [editing, setEditing] = useState(false);

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Property Console",
			employeeID: employeeID,
			notes: "Accessed the Property Console page",
		}).catch(() => {});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		handleAPICalls();
	}, []);

	useEffect(() => {
		createColumnData();
		createRowData();
	}, [properties]);

	// INPUT HANDLERS

	const handleAddClick = () => {
		setEditing(false);
		setEditAddDialogOpen(true);
	};

	const handleEditClick = (record) => {
		setRecordToEdit(record);
		setEditing(true);
		setEditAddDialogOpen(true);
	};

	const handleEditAddDialogClose = () => {
		setEditing(false);
		setEditAddDialogOpen(false);
		setRecordToEdit({});
		handleAPICalls();
	};

	const handleEditAddDialogCloseNoRefresh = () => {
		setEditing(false);
		setEditAddDialogOpen(false);
		setRecordToEdit({});
	};

	// API FUNCTIONS

	const handleAPICalls = async () => {
		setLoading(true);
		await fetchProperties();
		await fetchOffices();
		setLoading(false);
	};

	const fetchProperties = async (sortBy, sortDirection) => {
		let queryParams;
		if (sortBy && sortDirection) {
			queryParams = `sortBy=${sortBy}&sortDirection=${sortDirection}`;
		}

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				`/api/jobs/property/all?${queryParams}`
			);
			setProperties(res.data);
		} catch (error) {
			console.log(error);
		}
	};

	const fetchOffices = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/employees/offices/all"
			);
			setOffices(res.data);
		} catch (error) {
			console.log(error);
		}
	};

	// HELPER FUNCTIONS

	const createColumnData = () => {
		setPropertyColumns([
			{
				accessorKey: "name",
				header: () => <span>Name</span>,
				label: "Name",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "address",
				header: () => <span>Address</span>,
				label: "Address",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "dropbox_file_path",
				header: () => <span>DropBox File Path</span>,
				label: "DropBox File Path",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "oic",
				header: () => <span>OIC</span>,
				label: "OIC",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "created",
				header: () => <span>Created</span>,
				label: "Created",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "date",
				filterFn: dateSort,
			},
			{
				accessorKey: "edit",
				header: "",
				label: "Edit",
				cell: (props) => (
					<IconButton
						onClick={() =>
							handleEditClick(props.row.original.property)
						}
						disableRipple
						sx={{
							justifyContent: "flex-end",
						}}
					>
						<EditIcon fontSize="small" />
					</IconButton>
				),
				enableColumnFilter: false,
				enableSorting: false,
			},
		]);
	};

	const createRowData = () => {
		const formatAddress = (property) => {
			return [
				property.address_line1,
				property.address_line2,
				property.city,
				property.state,
				property.zip,
			]
				.filter((part) => part !== null && part !== "") // skip null or empty strings
				.join(" ");
		};

		setPropertyRows(
			properties.map((property) => ({
				name: property.name,
				address: formatAddress(property),
				dropbox_file_path: property.dropbox_file_path,
				oic: property.oic,
				created: convertToPST(property.created),
				property: property, // Store the entire property object for editing
			}))
		);
	};

	// RENDER
	return (
		<div className="PropertyConsole">
			<div className="PropertyConsoleHeader">
				<div className="PropertyConsoleHeaderTitle">
					Property Console
					<Button
						variant="contained"
						color="primary"
						onClick={handleAddClick}
						disabled={loading}
					>
						New Property
					</Button>
				</div>
			</div>
			{loading && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}
			{!loading && (
				<div className="tableContainerDiv">
					{properties.length === 0 && <J2NoResultsFound />}
					{properties.length > 0 &&
						propertyRows.length > 0 &&
						propertyColumns.length > 0 && (
							<TanStackTable
								columnData={propertyColumns}
								rowData={propertyRows}
							/>
						)}
				</div>
			)}
			<PropertyConsoleEditAddDialog
				open={editAddDialogOpen}
				onClose={handleEditAddDialogClose}
				onCloseNoRefresh={handleEditAddDialogCloseNoRefresh}
				recordToEdit={recordToEdit}
				editing={editing}
				properties={properties}
				offices={offices}
			/>
		</div>
	);
};

// EXPORT
export default PropertyConsole;
